import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Button, Grid, TextField, InputAdornment, IconButton, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InfoIcon from '@mui/icons-material/Info';
import { globalBetaPostService,globalPostService } from '../../utils/globalApiServices';
import { checkApiStatus, getGlobalCookie, setGlobalCookie } from '../../utils';
import { regexConstants } from '../../constants/regEx';
import { setForceChangePasswordModal, setForceChangePasswordModalData } from '../actions';
import appConfig from '../../config';

const ChangePasswordPopUp = () => {
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const open = useSelector(state => state.leases.forceChangePasswordModal);
	const selectedLessor = useSelector(state => state.leases.selectedLessor);
	const lessorAccess = JSON.parse(getGlobalCookie('lessorAccess'));
	const env = appConfig?.env?.key;
	const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState({ password_1: false, password_2: false, password_3: false });
	const [userInfo, setUserInfo] = useState({ old_password: '', password: '', confirm_password: '' });
	const [error, setError] = useState({});

	useEffect(() => {
		if (open) {
			setShowChangePasswordForm(false);
		}
	}, [open]);

	const onFieldChange = (keyParam, value) => {
		setUserInfo({ ...userInfo, [keyParam]: value });
	}

	const resetState = () => {
		setUserInfo({ old_password: '', password: '', confirm_password: '' });
		setError({});
		setShowPassword({ password_1: false, password_2: false, password_3: false });
	}

	const onClose = () => {
		dispatch(setForceChangePasswordModal(false));
		dispatch(setForceChangePasswordModalData(null));
	}

	const setCookies = (userData) => {
		const cookieData = JSON.stringify({ access: userData?.lessor_access_token, id: userData?.current_lessor?.id, refresh: "", environment: env, user: { id: userData?.user?.id, name: userData?.user?.name } });
		setGlobalCookie('lessorAccess', cookieData, 3);
		window.location.href = '/leases';
	}

	const switchLessor = () => {
		const payload = { lessor_id: (selectedLessor?.id || lessorAccess?.id), lite: true };
		globalPostService('lessor-switch/', payload).then(response => {
			if (checkApiStatus(response)) {
				setCookies(response?.data?.data);
			}
		});
	}

	const handleClose = () => {
		if (isLoading) return;
		resetState();
		onClose();
	}

	const onChangePwd = () => {
		const validationInputs = {
			old_password: userInfo?.old_password?.trim().length ? regexConstants.password.test(userInfo.old_password) ? '' : 'Please enter valid Old Password' : 'Old Password is required',
			password: userInfo?.password?.trim().length ? regexConstants.password.test(userInfo.password) ? '' : 'Please enter valid New Password' : 'New Password is required',
			confirm_password: userInfo?.confirm_password?.trim().length ? userInfo.confirm_password === userInfo.password ? '' : 'Confirm Password should be same as New Password' : 'Confirm Password is required',
		}
		if (Object.keys(validationInputs).every((key) => validationInputs[key] === '')) {
			setLoading(true);
			globalBetaPostService('api/password-change/', userInfo)
				.then(response => {
					if (checkApiStatus(response)) {
						enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
						resetState();
						onClose();
						switchLessor();
					} else {
						enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
					}
					setLoading(false);
				})
		} else {
			setError(validationInputs);
		}
	}

	if (!open) {
		return null;
	}

	if (!showChangePasswordForm) {
		return (
			<Dialog open={open} maxWidth='xs' fullWidth>
				<DialogContent>
					<Typography>Your password has expired. Please change your password</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowChangePasswordForm(true)} color='primary' variant='contained'>
						Change Password
					</Button>
				</DialogActions>
			</Dialog>
		)
	}

	return (
		<Dialog open={open} maxWidth='xs' fullWidth>
			<DialogTitle>Change Password</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={2}>
					<Grid item size={{ md: 12, xs: 12 }}>
						<TextField
							required
							type={showPassword.password_1 ? 'text' : 'password'}
							id='old_password'
							label='Old Password'
							value={userInfo?.old_password || ''}
							fullWidth
							margin="normal"
							error={error?.old_password ? true : false}
							helperText={error?.old_password || ''}
							onChange={(e) => onFieldChange('old_password', e.target.value)}
							onFocus={() => setError({ ...error, 'old_password': '' })}
							InputLabelProps={{ shrink: true }}
							InputProps={{
								endAdornment: <InputAdornment position="end">
									<IconButton
										size='small'
										onClick={() => setShowPassword({ ...showPassword, password_1: !showPassword.password_1 })}
									>
										{showPassword.password_1 ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>,
							}}
							variant='outlined'
							size='small'
							inputProps={{ maxLength: 100 }}
						/>
					</Grid>
					<Grid item size={{ md: 12, xs: 12 }}>
						<TextField
							required
							type={showPassword.password_2 ? 'text' : 'password'}
							id='password'
							label={
								<span>
									New Password
									<Tooltip placement='right' title={
										<>
											<ul className="list-unstyled">
												<li>Password Rules:</li>
												<li>- Password can contain a maximum of 20 characters</li>
												<li>- Password must contain minimum of 8 characters</li>
												<li>- Password must contain at least 1 number</li>
												<li>- Password must contain at least 1 uppercase character</li>
												<li>- Password must contain at least 1 lowercase character</li>
												<li>- Password must contain at least 1 special character</li>
											</ul>
										</>
									}
										arrow>
										<InfoIcon fontSize='small' style={{ verticalAlign: 'bottom', marginLeft: '3px' }} />
									</Tooltip>
								</span>
							}
							value={userInfo?.password || ''}
							fullWidth
							margin="normal"
							error={error?.password ? true : false}
							helperText={error?.password || ''}
							onChange={(e) => onFieldChange('password', e.target.value)}
							onFocus={() => setError({ ...error, 'password': '' })}
							InputLabelProps={{ shrink: true, style: { pointerEvents: 'auto' } }}
							InputProps={{
								endAdornment: <InputAdornment position="end">
									<IconButton
										size='small'
										onClick={() => setShowPassword({ ...showPassword, password_2: !showPassword.password_2 })}
									>
										{showPassword.password_2 ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>,
							}}
							variant='outlined'
							size='small'
							inputProps={{ maxLength: 20 }}
						/>
					</Grid>
					<Grid item size={{ md: 12, xs: 12 }}>
						<TextField
							required
							type={showPassword.password_3 ? 'text' : 'password'}
							id='confirm_password'
							label='Confirm Password'
							value={userInfo?.confirm_password || ''}
							fullWidth
							margin="normal"
							error={error?.confirm_password ? true : false}
							helperText={error?.confirm_password || ''}
							onChange={(e) => onFieldChange('confirm_password', e.target.value)}
							onFocus={() => setError({ ...error, 'confirm_password': '' })}
							InputLabelProps={{ shrink: true }}
							InputProps={{
								endAdornment: <InputAdornment position="end">
									<IconButton
										size='small'
										onClick={() => setShowPassword({ ...showPassword, password_3: !showPassword.password_3 })}
									>
										{showPassword.password_3 ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>,
							}}
							variant='outlined'
							size='small'
							inputProps={{ maxLength: 20 }}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
				<Button disabled={isLoading} onClick={onChangePwd} color='primary' variant='contained'>
					{isLoading ? <CircularProgress size={20} /> : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default ChangePasswordPopUp;
